"use client";

import axiosInstance from "@/app/api";
import {DashboardReportFilter} from "@/components/@filter";
import {NeoSelect} from "@/components/forms";
import {ColumnIcon, DownloadIcon, SearchIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {Button, SelectItem, Spinner} from "@nextui-org/react";
import Image from "next/image";
import {Fragment, ReactNode, useEffect, useMemo, useState} from "react";
import {FaLayerGroup} from "react-icons/fa";
import {
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiUserX,
  FiUsers
} from "react-icons/fi";

type CampusSummary = {
  totalStudents: number;
  present: number;
  absent: number;
  attendancePercentage: number;
};

type ClassAttendanceDate = {
  date: number;
  dateLabel: string;
  totalStudents: number;
  present: number;
  absent: number;
  attendancePercentage: number;
};

type ClassAttendance = {
  className: string;
  totalStudents: number;
  present: number;
  absent: number;
  attendancePercentage: number;
  dates: ClassAttendanceDate[];
};

type ClassDateGroup = ClassAttendanceDate & {
  classes: Array<ClassAttendance & {dateSummary: ClassAttendanceDate}>;
};

type GroupMode = "class-date" | "date-class";
type CampusColumnKey =
  | "dateSummary"
  | "totalStudents"
  | "present"
  | "absent"
  | "attendancePercentage"
  | "status";

const configurableTableColumns: Array<{
  align?: "center" | "left" | "right";
  key: CampusColumnKey;
  label: string;
}> = [
  {key: "dateSummary", label: "Date Range Summary"},
  {align: "right", key: "totalStudents", label: "Total Students"},
  {align: "right", key: "present", label: "Present"},
  {align: "right", key: "absent", label: "Absent"},
  {key: "attendancePercentage", label: "Attendance %"}
  // {key: "status", label: "Trend / Status"}
];
const groupingFilter = [
  {
    id: "class-date",
    label: "Grouped by: Class -> Date"
  },
  {
    id: "date-class",
    label: "Grouped by: Date -> Class"
  }
];
const emptySummary: CampusSummary = {
  totalStudents: 0,
  present: 0,
  absent: 0,
  attendancePercentage: 0
};

const numberFormatter = new Intl.NumberFormat("en-US");

const toNumber = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatNumber = (value: unknown) =>
  numberFormatter.format(toNumber(value));

const formatPercentage = (value: unknown) => `${toNumber(value).toFixed(2)}%`;

const formatDateRangeLabel = (from?: number, to?: number) => {
  if (!from || !to) return "Selected date range";

  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  });
  const fromLabel = formatter.format(new Date(from));
  const toLabel = formatter.format(new Date(to));

  return fromLabel === toLabel ? fromLabel : `${fromLabel} - ${toLabel}`;
};

const getDayCountLabel = (count: number) =>
  `${count} ${count === 1 ? "day" : "days"}`;

const getBranchIds = (dashboardfilter: any, fallbackBranch?: string | null) => {
  const branches = dashboardfilter?.instituteuserids;
  if (Array.isArray(branches) && branches.length > 0) return branches;
  return fallbackBranch ? [fallbackBranch] : [];
};

const buildDefaultRequest = ({
  dashboardfilter,
  filterContext,
  offset
}: {
  dashboardfilter: any;
  filterContext: any;
  offset: string;
}) => {
  const fromDate = new Date();
  const toDate = new Date();
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);

  return {
    userId: filterContext.client.institutionUserId,
    instituteId: filterContext.client.institutionId,
    pageNumber: 1,
    itemsPerPage: 100000,
    searchBy: "",
    sortColumn: "",
    sortValue: "",
    from: fromDate.getTime(),
    to: toDate.getTime(),
    filterType: "1",
    offset,
    staffClassId: [],
    filters: {
      branch: getBranchIds(
        dashboardfilter,
        filterContext.client.institutionUserId
      ),
      trackers: [],
      status: []
    }
  };
};

const getAttendanceStatus = (percentage: number) => {
  if (percentage >= 90) {
    return {
      label: "Excellent",
      className: "bg-[#e8f8ee] text-[#16833a]"
    };
  }
  if (percentage >= 70) {
    return {
      label: "Very Good",
      className: "bg-[#eaf1ff] text-[#2563eb]"
    };
  }
  if (percentage >= 50) {
    return {
      label: "Good",
      className: "bg-[#fff7e8] text-[#b75c00]"
    };
  }

  return {
    label: "Needs Improvement",
    className: "bg-[#fff0f2] text-[#f04455]"
  };
};

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

const buildDateGroups = (classes: ClassAttendance[]) => {
  const dateGroups = new Map<number, ClassDateGroup>();

  classes.forEach(classRow => {
    classRow.dates.forEach(dateSummary => {
      const dateKey = dateSummary.date;
      const current = dateGroups.get(dateKey) ?? {
        date: dateSummary.date,
        dateLabel: dateSummary.dateLabel,
        totalStudents: 0,
        present: 0,
        absent: 0,
        attendancePercentage: 0,
        classes: []
      };

      current.totalStudents += toNumber(dateSummary.totalStudents);
      current.present += toNumber(dateSummary.present);
      current.absent += toNumber(dateSummary.absent);
      current.classes.push({...classRow, dateSummary});

      dateGroups.set(dateKey, current);
    });
  });

  return Array.from(dateGroups.values())
    .map(group => ({
      ...group,
      attendancePercentage:
        group.totalStudents > 0
          ? Number(((group.present / group.totalStudents) * 100).toFixed(2))
          : 0
    }))
    .sort((a, b) => b.date - a.date);
};

const normalizeClassWiseData = (rows: any[]): ClassAttendance[] =>
  rows.map(row => ({
    className: row.className ?? "Unassigned",
    totalStudents: toNumber(row.totalStudents),
    present: toNumber(row.present),
    absent: toNumber(row.absent),
    attendancePercentage: toNumber(row.attendancePercentage),
    dates: Array.isArray(row.dates)
      ? row.dates.map((dateRow: any) => ({
          date: toNumber(dateRow.date),
          dateLabel: dateRow.dateLabel ?? "",
          totalStudents: toNumber(dateRow.totalStudents),
          present: toNumber(dateRow.present),
          absent: toNumber(dateRow.absent),
          attendancePercentage: toNumber(dateRow.attendancePercentage)
        }))
      : []
  }));

export const CampusAttendanceOverview = ({reportdata}: {reportdata: any}) => {
  const {filterContext, offset} = useNeoContext();
  const activeReport = reportdata?.activeReport;
  const [request, setRequest] = useState<any>(null);
  const [summary, setSummary] = useState<CampusSummary>(emptySummary);
  const [tableData, setTableData] = useState<ClassAttendance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [groupMode, setGroupMode] = useState<GroupMode>("class-date");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<CampusColumnKey>>(
    () => new Set(configurableTableColumns.map(column => column.key))
  );

  const branchKey = (reportdata?.dashboardfilter?.instituteuserids ?? []).join(
    ","
  );

  useEffect(() => {
    if (
      !filterContext?.client?.institutionUserId ||
      !filterContext?.client?.institutionId
    ) {
      return;
    }

    setRequest(
      buildDefaultRequest({
        dashboardfilter: reportdata?.dashboardfilter,
        filterContext,
        offset
      })
    );
  }, [
    branchKey,
    filterContext,
    filterContext?.client?.institutionId,
    filterContext?.client?.institutionUserId,
    offset,
    reportdata?.dashboardfilter
  ]);

  useEffect(() => {
    if (!activeReport?.url || !request?.from || !request?.to) return;

    let isCurrentRequest = true;
    const fetchOverview = async () => {
      setIsLoading(true);
      setErrorMessage("");

      const response = await axiosInstance().post(activeReport.url, request);
      if (!isCurrentRequest) return;

      const responseData = response?.data;
      if (responseData?.status !== "success") {
        setSummary(emptySummary);
        setTableData([]);
        setErrorMessage("Unable to load campus attendance overview");
        return;
      }

      setSummary(responseData?.data?.summary ?? emptySummary);
      setTableData(
        normalizeClassWiseData(responseData?.data?.classWiseData ?? [])
      );
    };

    fetchOverview()
      .catch(() => {
        if (!isCurrentRequest) return;
        setSummary(emptySummary);
        setTableData([]);
        setErrorMessage("Unable to load campus attendance overview");
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeReport?.url, request]);

  const filteredClasses = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return tableData;

    return tableData.filter(row =>
      row.className.toLowerCase().includes(search)
    );
  }, [searchValue, tableData]);

  const dateGroups = useMemo(
    () => buildDateGroups(filteredClasses),
    [filteredClasses]
  );

  useEffect(() => {
    const keys =
      groupMode === "class-date"
        ? filteredClasses.map(row => `class-${row.className}`)
        : dateGroups.map(group => `date-${group.date}`);

    setExpandedGroups(new Set(keys));
  }, [dateGroups, filteredClasses, groupMode]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleVisibleColumn = (key: CampusColumnKey) => {
    setVisibleColumns(current => {
      const next = new Set(current);

      if (next.has(key)) {
        if (next.size === 1) return current;
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const applyFilter = (filterData: any) => {
    const nextFilters = {
      ...(request?.filters ?? {}),
      ...(filterData?.filters ?? {}),
      branch: getBranchIds(
        reportdata?.dashboardfilter,
        filterContext.client.institutionUserId
      )
    };

    setRequest((current: any) => ({
      ...current,
      ...filterData,
      filters: nextFilters,
      offset,
      staffClassId: current?.staffClassId ?? []
    }));
    setIsFilterOpen(false);
  };

  const exportRows = async () => {
    const {utils, writeFileXLSX} = await import("xlsx");
    const rows = filteredClasses.flatMap(classRow => {
      if (!classRow.dates.length) {
        return [
          {
            Class: classRow.className,
            Date: "",
            "Total Students": classRow.totalStudents,
            Present: classRow.present,
            Absent: classRow.absent,
            "Attendance %": classRow.attendancePercentage,
            Status: getAttendanceStatus(toNumber(classRow.attendancePercentage))
              .label
          }
        ];
      }

      return classRow.dates.map(dateSummary => ({
        Class: classRow.className,
        Date: dateSummary.dateLabel,
        "Total Students": dateSummary.totalStudents,
        Present: dateSummary.present,
        Absent: dateSummary.absent,
        "Attendance %": dateSummary.attendancePercentage,
        Status: getAttendanceStatus(toNumber(dateSummary.attendancePercentage))
          .label
      }));
    });

    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Campus Attendance");
    writeFileXLSX(
      workbook,
      `Campus Attendance-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const rangeLabel = formatDateRangeLabel(request?.from, request?.to);
  const visibleColumnCount =
    1 +
    configurableTableColumns.filter(column => visibleColumns.has(column.key))
      .length;

  return (
    <section className="flex min-h-[calc(100vh-63px)] w-full flex-col overflow-hidden bg-white px-5 py-5 text-[#1f2937]">
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {activeReport?.filters?.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  aria-label="Open filters"
                  onClick={() => {
                    setIsFilterOpen(open => !open);
                    setIsColumnMenuOpen(false);
                  }}
                  className="flex h-7 w-7 items-center justify-center"
                >
                  <Image
                    src="/icons/filter.svg"
                    alt=""
                    width={20}
                    height={20}
                    className="h-5 w-5"
                  />
                </button>
                {isFilterOpen && (
                  <div className="absolute left-0 z-50 mt-2 w-auto">
                    <div className="rounded-md border bg-white p-5 shadow-lg">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={() => setIsFilterOpen(false)}
                        className="absolute right-2 top-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </Button>
                      <DashboardReportFilter
                        activeFilters={activeReport.filters}
                        innerFilters={activeReport.innerFilters}
                        oldFilter={request}
                        onSubmit={applyFilter}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col">
              <h5 className="text-xl">
                {activeReport?.menu ?? "Campus Attendance"}
              </h5>
              <h3 className="text-xs text-gray-400">
                Campus attendance report generated from UHF and Boimetric
                records.
              </h3>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-small text-default-400">
            Total Data : {formatNumber(summary.totalStudents)}
          </span>
          <div className="flex items-center justify-between">
            <label className="relative block w-[280px] max-w-[calc(100vw-140px)]">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1f2937]" />
              <input
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                placeholder="Search..."
                className="h-10 w-full rounded-full border border-[#dfe1e6] bg-white pl-9 pr-3 text-sm text-[#1f2937] shadow-sm outline-none transition placeholder:text-[#667085] focus:border-[#9aa4b2]"
              />
            </label>
            <button
              type="button"
              aria-label="Export campus attendance"
              onClick={exportRows}
              className="flex h-9 w-9 cursor-pointer items-center justify-center px-2 text-[#111827]"
            >
              <DownloadIcon size={24} />
            </button>
            <div className="relative">
              <button
                type="button"
                aria-label="Show table columns"
                onClick={() => {
                  setIsColumnMenuOpen(open => !open);
                  setIsFilterOpen(false);
                }}
                className="flex h-9 w-9 cursor-pointer items-center justify-center px-2"
              >
                <ColumnIcon size={24} />
              </button>
              {isColumnMenuOpen && (
                <div className="absolute right-0 z-40 mt-2 w-56 rounded-md border bg-white p-3 shadow-lg">
                  {configurableTableColumns.map(column => (
                    <label
                      key={column.key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold text-[#344054] hover:bg-[#f5f7fb]"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(column.key)}
                        onChange={() => toggleVisibleColumn(column.key)}
                        className="h-4 w-4"
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            icon={<FiUsers />}
            description="Students included in selected filters"
            label="Total Students"
            value={formatNumber(summary.totalStudents)}
            tone="blue"
          />
          <SummaryCard
            icon={<FiCheckCircle />}
            description="Students marked present"
            label="Present"
            value={formatNumber(summary.present)}
            tone="green"
          />
          <SummaryCard
            icon={<FiUserX />}
            description="Students marked absent"
            label="Absent"
            value={formatNumber(summary.absent)}
            tone="red"
          />
          <SummaryCard
            icon={<FiBarChart2 />}
            description="Present students out of total"
            label="Attendance %"
            value={formatPercentage(summary.attendancePercentage)}
            tone="purple"
          />
        </div>
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex rounded-lg  p-1 text-sm font-bold text-[#526078]">
            <NeoSelect
              label=""
              size="md"
              className="w-[260px]"
              placeholder="Group by"
              selectedKeys={groupMode ? [groupMode] : []}
              onSelectionChange={keys => {
                const first = Array.isArray(keys)
                  ? keys[0]
                  : Array.from(keys as Set<unknown>)[0];

                const value =
                  first !== undefined && first !== null
                    ? String(first)
                    : "class-date";

                setGroupMode(value as "class-date" | "date-class");
              }}
              startContent={<FaLayerGroup className="h-4 w-4 text-[#1e2a44]" />}
            >
              {groupingFilter.map(item => (
                <SelectItem key={item.id} value={item.id}>
                  {item.label}
                </SelectItem>
              ))}
            </NeoSelect>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#dfe6f1] bg-white shadow-sm">
          <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left text-xs">
            <thead className="sticky top-0 z-10 bg-[#f8fafc] text-[#475467]">
              <tr>
                <TableHead>Class / Date</TableHead>
                {configurableTableColumns.map(
                  column =>
                    visibleColumns.has(column.key) && (
                      <TableHead key={column.key} align={column.align}>
                        {column.label}
                      </TableHead>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={visibleColumnCount}>
                    <div className="flex min-h-64 items-center justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td colSpan={visibleColumnCount}>
                    <EmptyState message={errorMessage} />
                  </td>
                </tr>
              ) : groupMode === "class-date" ? (
                <ClassDateRows
                  expandedGroups={expandedGroups}
                  rangeLabel={rangeLabel}
                  rows={filteredClasses}
                  toggleGroup={toggleGroup}
                  visibleColumns={visibleColumns}
                />
              ) : (
                <DateClassRows
                  expandedGroups={expandedGroups}
                  rows={dateGroups}
                  toggleGroup={toggleGroup}
                  visibleColumns={visibleColumns}
                />
              )}
            </tbody>
          </table>
          {!isLoading && !errorMessage && filteredClasses.length === 0 && (
            <EmptyState message="No campus attendance records found" />
          )}
        </div>
      </div>
    </section>
  );
};

const SummaryCard = ({
  description,
  icon,
  label,
  tone,
  value
}: {
  description: string;
  icon: ReactNode;
  label: string;
  tone: "blue" | "green" | "purple" | "red";
  value: string;
}) => {
  const toneStyles = {
    blue: "bg-[#eef4ff] text-[#2563eb]",
    green: "bg-[#e8f8ee] text-[#16833a]",
    purple: "bg-[#f0eaff] text-[#7047d9]",
    red: "bg-[#fff0f2] text-[#f04455]"
  };

  return (
    <article className="rounded-lg border border-[#e5eaf3] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${toneStyles[tone]} [&_svg]:h-5 [&_svg]:w-5`}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-bold text-[#667085]">{label}</p>
          <p className="mt-1 truncate text-xl font-bold text-[#1f2937]">
            {value}
          </p>
        </div>
      </div>
      <p className="mt-3 truncate text-[11px] font-semibold text-[#98a2b3]">
        {description}
      </p>
    </article>
  );
};

const ClassDateRows = ({
  expandedGroups,
  rangeLabel,
  rows,
  toggleGroup,
  visibleColumns
}: {
  expandedGroups: Set<string>;
  rangeLabel: string;
  rows: ClassAttendance[];
  toggleGroup: (key: string) => void;
  visibleColumns: Set<CampusColumnKey>;
}) => {
  return (
    <>
      {rows.map(row => {
        const groupKey = `class-${row.className}`;
        const canToggle = row.dates.length > 1;
        const isExpanded = canToggle && expandedGroups.has(groupKey);

        return (
          <Fragment key={groupKey}>
            <GroupRow
              breakdownLabel={getDayCountLabel(row.dates.length)}
              canToggle={canToggle}
              dateLabel={rangeLabel}
              isExpanded={isExpanded}
              metrics={row}
              name={row.className}
              onToggle={() => toggleGroup(groupKey)}
              visibleColumns={visibleColumns}
            />
            {canToggle &&
              isExpanded &&
              row.dates.map(dateSummary => (
                <DetailRow
                  key={`${row.className}-${dateSummary.date}`}
                  dateLabel={dateSummary.dateLabel}
                  metrics={dateSummary}
                  name=""
                  visibleColumns={visibleColumns}
                />
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

const DateClassRows = ({
  expandedGroups,
  rows,
  toggleGroup,
  visibleColumns
}: {
  expandedGroups: Set<string>;
  rows: ClassDateGroup[];
  toggleGroup: (key: string) => void;
  visibleColumns: Set<CampusColumnKey>;
}) => {
  return (
    <>
      {rows.map(group => {
        const groupKey = `date-${group.date}`;
        const canToggle = group.classes.length > 1;
        const isExpanded = canToggle && expandedGroups.has(groupKey);

        return (
          <Fragment key={groupKey}>
            <GroupRow
              breakdownLabel={`${group.classes.length} classes`}
              canToggle={canToggle}
              dateLabel="Daily Summary"
              isExpanded={isExpanded}
              metrics={group}
              name={group.dateLabel}
              onToggle={() => toggleGroup(groupKey)}
              visibleColumns={visibleColumns}
            />
            {canToggle &&
              isExpanded &&
              group.classes.map(row => (
                <DetailRow
                  key={`${group.date}-${row.className}`}
                  dateLabel={row.dateSummary.dateLabel}
                  metrics={row.dateSummary}
                  name={row.className}
                  visibleColumns={visibleColumns}
                />
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

const GroupRow = ({
  breakdownLabel,
  canToggle = true,
  dateLabel,
  isExpanded,
  metrics,
  name,
  onToggle,
  visibleColumns
}: {
  breakdownLabel: string;
  canToggle?: boolean;
  dateLabel: string;
  isExpanded: boolean;
  metrics: Omit<ClassAttendance, "className" | "dates">;
  name: string;
  onToggle: () => void;
  visibleColumns: Set<CampusColumnKey>;
}) => {
  return (
    <tr className="bg-white text-[#344054] hover:bg-[#f8fafc]">
      <TableCell>
        {canToggle ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 items-center gap-2 font-bold"
          >
            {isExpanded ? (
              <FiChevronDown className="h-4 w-4" />
            ) : (
              <FiChevronRight className="h-4 w-4" />
            )}
            <span title={name} className="block max-w-[220px] truncate">
              {name || "Unassigned"}
            </span>
          </button>
        ) : (
          <div className="flex min-w-0 items-center font-bold">
            <span title={name} className="block max-w-[220px] truncate">
              {name || "Unassigned"}
            </span>
          </div>
        )}
      </TableCell>
      {visibleColumns.has("dateSummary") && (
        <TableCell>
          <div className="font-bold">{dateLabel}</div>
          <div className="text-[11px] font-semibold text-[#98a2b3]">
            {breakdownLabel}
          </div>
        </TableCell>
      )}
      <MetricCells metrics={metrics} visibleColumns={visibleColumns} />
    </tr>
  );
};

const DetailRow = ({
  dateLabel,
  metrics,
  name,
  visibleColumns
}: {
  dateLabel: string;
  metrics: ClassAttendanceDate;
  name: string;
  visibleColumns: Set<CampusColumnKey>;
}) => {
  return (
    <tr className="bg-[#fbfcff] text-[#526078]">
      <TableCell>
        {name && (
          <span
            title={name}
            className="block max-w-[220px] truncate pl-6 font-bold"
          >
            {name}
          </span>
        )}
      </TableCell>
      {visibleColumns.has("dateSummary") && (
        <TableCell>
          <div className="flex items-center gap-2 pl-6 font-semibold">
            <FiCalendar className="h-3.5 w-3.5 text-[#98a2b3]" />
            {dateLabel}
          </div>
        </TableCell>
      )}
      <MetricCells metrics={metrics} visibleColumns={visibleColumns} />
    </tr>
  );
};

const MetricCells = ({
  metrics,
  visibleColumns
}: {
  metrics: Omit<ClassAttendance, "className" | "dates"> | ClassAttendanceDate;
  visibleColumns: Set<CampusColumnKey>;
}) => {
  const percentage = toNumber(metrics.attendancePercentage);
  const status = getAttendanceStatus(percentage);

  return (
    <>
      {visibleColumns.has("totalStudents") && (
        <TableCell align="right">
          {formatNumber(metrics.totalStudents)}
        </TableCell>
      )}
      {visibleColumns.has("present") && (
        <TableCell align="right" className="text-[#16833a]">
          {formatNumber(metrics.present)}
        </TableCell>
      )}
      {visibleColumns.has("absent") && (
        <TableCell align="right" className="text-[#f04455]">
          {formatNumber(metrics.absent)}
        </TableCell>
      )}
      {visibleColumns.has("attendancePercentage") && (
        <TableCell>
          <div className="flex min-w-[130px] items-center gap-2">
            <span className="w-12 text-right font-bold">
              {formatPercentage(percentage)}
            </span>
            <span className="h-1.5 flex-1 rounded-full bg-[#dbe4f0]">
              <span
                className="block h-1.5 rounded-full bg-[#2563eb]"
                style={{width: `${clampPercentage(percentage)}%`}}
              />
            </span>
          </div>
        </TableCell>
      )}
      {visibleColumns.has("status") && (
        <TableCell>
          <span
            className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${status.className}`}
          >
            {status.label}
          </span>
        </TableCell>
      )}
    </>
  );
};

const TableHead = ({
  align = "left",
  children
}: {
  align?: "center" | "left" | "right";
  children: ReactNode;
}) => {
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";

  return (
    <th
      className={`border-b border-[#e5eaf3] px-3 py-3 text-xs font-bold ${alignClass}`}
    >
      {children}
    </th>
  );
};

const TableCell = ({
  align = "left",
  children,
  className = ""
}: {
  align?: "center" | "left" | "right";
  children: ReactNode;
  className?: string;
}) => {
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";

  return (
    <td
      className={`border-b border-[#eef2f7] px-3 py-3 align-middle font-semibold ${alignClass} ${className}`}
    >
      {children}
    </td>
  );
};

const EmptyState = ({message}: {message: string}) => {
  return (
    <div className="flex min-h-48 items-center justify-center px-4 py-8 text-center text-sm font-semibold text-[#667085]">
      {message}
    </div>
  );
};
